import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EnvService } from '@lib/env';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { UriBuilder } from 'uribuilder';
import { ListInvoiceFilter } from './list/types/ListInvoiceFilter';
import { ListInvoiceStatus } from './list/types/ListInvoiceStatus';
import { IimPagination } from '@iim-angular/components';
import { HttpCacheKeyPrefix, HttpExtras, SortDirection } from '@lib/core';
import { InvoiceDetail } from './detail/types/InvoiceDetail';
import { ITestInvoiceNumberResponse } from './types/ITestInvoiceNumberResponse';
import { ICreateInvoiceCommandDto } from './create-standalone/types/ICreateStandaloneInvoiceCommand';
import { IInvoiceHistory, IStandaloneInvoiceDetailDto } from './detail/types/IStandaloneInvoiceDetail';
import { INewListInvoicesResponse } from './types/INewListInvoicesResponse';
import { IStandaloneInvoicePreviewDto } from './list/types/IStandaloneInvoicePreview';
import { IInvoiceDraft } from './create-standalone/types/IInvoiceDraftDto';
import { IAddInvoiceCommentRequest } from './types/IAddInvoiceCommentRequest';
import { IOrderCandidate, IOrderCandidateFilter, IOrderCandidateHeader } from './types/IOrderCandidate';
import { IProcessInstance, ProcessInstance } from '@lib/workflow-util';
import { ListInvoiceSorting } from './list/types/ListInvoiceSorting';
import { CustomerDepartmentDto } from '@lib/department/ui';
import { InvoiceApprovalInfo } from './types/InvoiceApprovalInfo';
import { IApprovalDto } from '@lib/approval';
import { AccountingDataRequest } from './workflow/types/AccountingDataRequest';
import { AccountingChange } from '@lib/accounting/types';
import { ISettingAllAccountings } from './types/ISettingAllAccountings';
import { IInvoiceCreateResponse } from './create-standalone/types/IInvoiceCreateResponse';
import { InvoiceSettingsDto } from './types/InvoiceSettingsDto';
import { IInvoiceOrderComparisonDto } from './detail/types/IInvoiceOrderComparison';
import { InvoiceOrderComparison } from './detail/types/InvoiceOrderComparison';

export interface IInvoiceValidationResponse {
    valid: boolean;
    errors?: Array<string | { message?: string }>;
}

@Injectable({
    providedIn: 'root',
})
export class InvoiceDataService {
    private readonly http = inject(HttpClient);
    private readonly env = inject(EnvService);

    public markInvoicesPaid(id: string): Observable<void> {
        const uri = this.env.getBase('invoices', 'v2', id, 'pay');

        return this.http.post<void>(
            uri.toString(),
            {},
            {
                params: HttpExtras.PRUNE_CACHE_WITH_PREFIX(HttpCacheKeyPrefix.INVOICE),
            },
        );
    }

    public getListNew(
        filter: ListInvoiceFilter,
        pagination: IimPagination,
        sortDirection: SortDirection,
        sortBy: ListInvoiceSorting = ListInvoiceSorting.CREATE_DATE,
    ): Observable<INewListInvoicesResponse> {
        const uri = this.env.getBase('invoices', 'v2', 'search');

        if (filter.generalSearch) {
            uri.query.searchQuery = filter.generalSearch;
        }
        if (filter.documentDateFrom) {
            uri.query.documentDateFrom = filter.documentDateFrom;
        }
        if (filter.documentDateTo) {
            uri.query.documentDateTo = filter.documentDateTo;
        }
        if (filter.createDateFrom) {
            uri.query.createDateFrom = filter.createDateFrom;
        }
        if (filter.createDateTo) {
            uri.query.createDateTo = filter.createDateTo;
        }
        if (filter.status !== ListInvoiceStatus.ALL) {
            uri.query.status = filter.status;
        }
        uri.query.page = pagination.page - 1;
        uri.query.pageSize = pagination.elementsPerPage;
        uri.query.sortDirection = sortDirection;
        uri.query.sortBy = sortBy;

        return this.http.get<INewListInvoicesResponse>(uri.toString(), {
            params: HttpExtras.CACHE_KEY_PREFIX(HttpCacheKeyPrefix.INVOICE),
        });
    }

    /**
     * Creates a standalone invoice with dummy data
     * @returns Observable with dummy data for a standalone invoice including items
     */
    public getStandaloneInitialData(deliveryReceiptIds: number[] = []): Observable<IInvoiceDraft> {
        const url: UriBuilder = this.env.getBase('invoices', 'v2', 'draft');
        url.query = {
            deliveryIds: deliveryReceiptIds,
        };

        return this.http.post<IInvoiceDraft>(
            url.toString(),
            {},
            {
                params: HttpExtras.IGNORE_CACHE,
            },
        );
    }

    public getStandaloneInitialDataWithInvoiceId(invoiceId: string): Observable<IInvoiceDraft> {
        const url: UriBuilder = this.env.getBase('invoices', 'v2', invoiceId, 'draft');

        return this.http.get<IInvoiceDraft>(url.toString(), {
            params: HttpExtras.IGNORE_CACHE,
        });
    }

    public getDepartments(): Observable<CustomerDepartmentDto[]> {
        const url: UriBuilder = this.env.getBase('customer', 'company', 'department');

        return this.http.get<CustomerDepartmentDto[]>(url.toString(), {
            params: HttpExtras.IGNORE_CACHE,
        });
    }

    public onSaveDraft(
        command: ICreateInvoiceCommandDto,
        invoiceId: string,
        files?: FormData,
        scannedImages?: FormData,
        taskId?: string,
    ): Observable<void> {
        const url: UriBuilder = this.env.getBase('invoices', 'v2', invoiceId);

        if (taskId) {
            url.query.taskId = taskId;
        }

        const formData = this.generateSavingFormData(command, files, scannedImages);

        return this.http.put<void>(url.toString(), formData, {
            params: HttpExtras.PRUNE_CACHE_WITH_PREFIX(HttpCacheKeyPrefix.INVOICE),
        });
    }

    public onSaveForApproval(
        command: ICreateInvoiceCommandDto,
        invoiceId: string,
        files?: FormData,
        scannedImages?: FormData,
    ): Observable<void> {
        const url: UriBuilder = this.env.getBase('invoices', 'v2', invoiceId, 'submit');
        const formData = this.generateSavingFormData(command, files, scannedImages);

        return this.http.put<void>(url.toString(), formData, {
            params: HttpExtras.PRUNE_CACHE_WITH_PREFIX(HttpCacheKeyPrefix.INVOICE),
        });
    }

    public createStandaloneInvoice(
        command: ICreateInvoiceCommandDto,
        files?: FormData,
        scannedImages?: FormData,
    ): Observable<IInvoiceCreateResponse> {
        const url: UriBuilder = this.env.getBase('invoices', 'v2');
        const formData = this.generateSavingFormData(command, files, scannedImages);

        return this.http.post<IInvoiceCreateResponse>(url.toString(), formData, {
            params: HttpExtras.PRUNE_CACHE_WITH_PREFIX(HttpCacheKeyPrefix.INVOICE),
        });
    }

    public getStandaloneInvoice(id: string): Observable<InvoiceDetail> {
        const url: UriBuilder = this.env.getBase('invoices', 'v2', id);

        return this.http
            .get<IStandaloneInvoiceDetailDto>(url.toString(), {
                params: HttpExtras.IGNORE_CACHE,
            })
            .pipe(map((dto) => InvoiceDetail.fromStandalone(dto)));
    }

    /**
     * Item-level deviations of the invoice from the orders it bills (UC_2026). Read-only; the result
     * changes nothing on the invoice.
     *
     * Cache is bypassed for the same reason the detail is: after an edit in the approval form a
     * cached comparison would describe the invoice as it was before the edit.
     *
     * Inside the approval form the task is passed along: an approver is often set up without an
     * invoice role and is allowed to see this invoice through the task alone. Errors are handled by
     * the caller — the comparison is a hint, and a failed read must not put an error message over the
     * approval form.
     */
    public getOrderComparison(id: string, taskId?: string): Observable<InvoiceOrderComparison> {
        const url: UriBuilder = this.env.getBase('invoices', 'v2', id, 'order-comparison');
        if (taskId) {
            url.query.taskId = taskId;
        }

        return this.http
            .get<IInvoiceOrderComparisonDto>(url.toString(), {
                params: new HttpExtras({ ignoreCache: true, customErrorhandling: true }),
            })
            .pipe(map((dto) => new InvoiceOrderComparison(dto)));
    }

    public getStandaloneInvoicePreview(id: string): Observable<IStandaloneInvoicePreviewDto> {
        const url: UriBuilder = this.env.getBase('invoices', 'v2', id, 'preview');

        return this.http.get<IStandaloneInvoicePreviewDto>(url.toString());
    }

    public addComment(
        invoiceId: string,
        comment: string,
        isInternal: boolean,
        files: FormDataEntryValue[],
        scannedImages: FormDataEntryValue[],
    ): Observable<IInvoiceHistory> {
        const url: UriBuilder = this.env.getBase('invoices', 'v2', invoiceId, 'comment');

        const formData = new FormData();

        const command: IAddInvoiceCommentRequest = {
            comment: comment.trim(),
            isInternal,
        };

        // Always include the 'data' part as JSON blob
        formData.append(
            'data',
            new Blob([JSON.stringify(command)], {
                type: 'application/json',
            }),
        );

        this.appendFilesToFormData(files, formData, scannedImages);

        return this.http.post<IInvoiceHistory>(url.toString(), formData, {
            params: HttpExtras.PRUNE_CACHE_WITH_PREFIX(HttpCacheKeyPrefix.INVOICE),
        });
    }

    public deleteComment(invoiceId: string, commentId: string): Observable<IInvoiceHistory> {
        const url: UriBuilder = this.env.getBase('invoices', 'v2', invoiceId, 'comment', commentId);

        return this.http.delete<IInvoiceHistory>(url.toString(), {
            params: HttpExtras.PRUNE_CACHE_WITH_PREFIX(HttpCacheKeyPrefix.INVOICE),
        });
    }

    saveAccountingValue(accountingData: AccountingDataRequest): Observable<void> {
        const { invoiceId, invoiceItemId, accountingChangeData } = accountingData;

        const body = {
            _value: {
                id: String(accountingChangeData.value?.id ?? accountingChangeData.accountAssignmentId),
                key: String(accountingChangeData.value?.key ?? accountingChangeData.accountAssignmentKey),
                name: accountingChangeData.value?.name ?? '',
                description: accountingChangeData.value?.description ?? '',
            },
            customAccountingTypeId: accountingChangeData.customAccountingTypeId ?? null,
            type: accountingChangeData.type,
        };

        return this.http.put<void>(
            this.env.getBase('invoices', 'v2', invoiceId, 'item-accounting', invoiceItemId).toString(),
            body,
            { params: HttpExtras.PRUNE_CACHE_WITH_PREFIX(HttpCacheKeyPrefix.INVOICE) },
        );
    }

    deleteAccountingValue(accountingDeleteData: AccountingDataRequest): Observable<void> {
        const { invoiceId, invoiceItemId, accountingChangeData } = accountingDeleteData;
        const body = {
            _value: {
                id: String(accountingChangeData.value?.id ?? accountingChangeData.accountAssignmentId),
                key: String(accountingChangeData.value?.key ?? accountingChangeData.accountAssignmentKey),
                name: accountingChangeData.value?.name ?? '',
                description: accountingChangeData.value?.description ?? '',
            },
            customAccountingTypeId: accountingChangeData.customAccountingTypeId ?? null,
            type: accountingChangeData.type,
        };

        return this.http.delete<void>(
            this.env.getBase('invoices', 'v2', invoiceId, 'item-accounting', invoiceItemId).toString(),
            { body, params: HttpExtras.PRUNE_CACHE_WITH_PREFIX(HttpCacheKeyPrefix.INVOICE) },
        );
    }

    saveItemMaterialGroup(invoiceId: string, invoiceItemId: string, materialGroupCode: string): Observable<void> {
        return this.http.put<void>(
            this.env.getBase('invoices', 'v2', invoiceId, 'item-material-group', invoiceItemId).toString(),
            { materialGroupCode },
            { params: HttpExtras.PRUNE_CACHE_WITH_PREFIX(HttpCacheKeyPrefix.INVOICE) },
        );
    }

    deleteItemMaterialGroup(invoiceId: string, invoiceItemId: string): Observable<void> {
        return this.http.delete<void>(
            this.env.getBase('invoices', 'v2', invoiceId, 'item-material-group', invoiceItemId).toString(),
            { params: HttpExtras.PRUNE_CACHE_WITH_PREFIX(HttpCacheKeyPrefix.INVOICE) },
        );
    }

    /**
     * Searches orders the given invoice item can be linked to (UC_2020, EPROC-12024).
     *
     * The result is already narrowed by the backend to the invoice's company, supplier and company
     * code; a blank term returns the unfiltered — but limited — list. The approval task lets its
     * assignee search without an invoice role.
     */
    searchOrderCandidates(
        invoiceId: string,
        filter: IOrderCandidateFilter,
        header: IOrderCandidateHeader | null = null,
        taskId: string | null = null,
    ): Observable<IOrderCandidate[]> {
        const url: UriBuilder = this.env.getBase('invoices', 'v2', invoiceId, 'order-candidates');
        // Empty date bounds are left out entirely; an empty query value would reach the backend as a
        // blank string and fail to parse as a date.
        url.query = {
            q: filter.term,
            includeClosed: String(filter.includeClosed),
            ...(filter.orderDateFrom ? { orderDateFrom: filter.orderDateFrom } : {}),
            ...(filter.orderDateTo ? { orderDateTo: filter.orderDateTo } : {}),
            ...(header ? { supplierCompanyId: header.supplierCompanyId } : {}),
            ...(header?.companyCodeId ? { companyCodeId: header.companyCodeId } : {}),
            ...(taskId ? { taskId } : {}),
        };

        return this.http.get<IOrderCandidate[]>(url.toString(), { params: HttpExtras.IGNORE_CACHE });
    }

    setAllAccountingValues(accountingChanges: AccountingChange[], invoiceId: string) {
        const payload = this.mappingAllAccountingChanges(accountingChanges);
        return this.http.put<void>(this.env.getBase('invoices', 'v2', invoiceId, 'accounting').toString(), payload, {
            params: HttpExtras.PRUNE_CACHE_WITH_PREFIX(HttpCacheKeyPrefix.INVOICE),
        });
    }

    private mappingAllAccountingChanges(accountingChanges: AccountingChange[]) {
        const allAccountingChanges: ISettingAllAccountings[] | undefined = [];

        for (const accountingChange of accountingChanges) {
            // The API endpoint does not take the accountingChanges out of the box first we need to map it to the correct format
            const body: ISettingAllAccountings = {
                _value: {
                    id: String(accountingChange.value?.id ?? accountingChange.accountAssignmentId),
                    key: String(accountingChange.value?.key ?? accountingChange.accountAssignmentKey),
                    name: accountingChange.value?.name ?? '',
                    description: accountingChange.value?.description ?? '',
                },
                customAccountingTypeId: accountingChange.customAccountingTypeId ?? null,
                type: accountingChange.type,
            };
            allAccountingChanges.push(body);
        }
        return allAccountingChanges;
    }

    private appendFilesToFormData(
        files: FormDataEntryValue[],
        formData: FormData,
        scannedImages: FormDataEntryValue[],
    ) {
        // Append files if available
        if (files) {
            files.forEach((file: File) => {
                formData.append('files', file, file.name);
            });
        }

        // Append scanned images if available
        if (scannedImages) {
            scannedImages.forEach((file: File) => {
                formData.append('images', file, file.name);
            });
        }
    }

    /**
     * Checks if a given invoice number is available for the specified supplier company.
     *
     * @param supplierCompanyId - The unique identifier of the supplier company.
     * @param invoiceNumber - The invoice number to be checked for availability.
     * @param excludeInvoiceId - Optional invoice ID to exclude from the check.
     * @return {Observable<ITestInvoiceNumberResponse>} An observable containing the response indicating the availability of the invoice number.
     */
    public checkIfInvoiceNumberIsAvailable(
        supplierCompanyId: string,
        invoiceNumber: string,
        excludeInvoiceId?: string,
    ): Observable<ITestInvoiceNumberResponse> {
        const url: UriBuilder = this.env.getBase('invoices', 'v2', 'check-invoice-number');
        url.query = {
            invoiceNumber,
            supplierCompanyId,
        };

        if (excludeInvoiceId) {
            url.query.excludeInvoiceId = excludeInvoiceId;
        }

        return this.http.get<ITestInvoiceNumberResponse>(url.toString(), { params: HttpExtras.IGNORE_CACHE });
    }

    getInvoiceSettings(): Observable<InvoiceSettingsDto> {
        const uri: UriBuilder = this.env.getBase('invoices', 'v2', 'settings');
        return this.http.get<InvoiceSettingsDto>(uri.toString());
    }

    getOpenProcessesForInvoice(invoiceId: string): Observable<ProcessInstance[]> {
        const uri: UriBuilder = this.env.getBase('invoices', 'task', 'open-processes');
        uri.query = {
            invoiceId,
        };

        return this.http
            .get<IProcessInstance[]>(uri.toString(), { params: HttpExtras.IGNORE_CACHE })
            .pipe(map((instances) => instances.map((instance) => new ProcessInstance(instance))));
    }

    getBpmnXmlForProcessDefinition(processDefinitionId: string): Observable<string> {
        const uri: UriBuilder = this.env.getBase('invoices', 'task', 'open-processes', processDefinitionId, 'bpmn');
        return this.http.get<{ bpmn20Xml: string }>(uri.toString()).pipe(map((record) => record.bpmn20Xml));
    }

    private generateSavingFormData(command: ICreateInvoiceCommandDto, files: FormData, scannedImages: FormData) {
        const formData = new FormData();

        // Always include the 'data' part as JSON blob
        formData.append(
            'data',
            new Blob([JSON.stringify(command)], {
                type: 'application/json',
            }),
        );

        // Append files if available
        if (files) {
            files.forEach((file: File) => {
                formData.append('files', file, file.name);
            });
        }

        // Append scanned images if available
        if (scannedImages) {
            scannedImages.forEach((file: File) => {
                formData.append('images', file, file.name);
            });
        }
        return formData;
    }

    public getApprovalInfoFor(invoiceId: string): Observable<InvoiceApprovalInfo> {
        const uri: UriBuilder = this.env.getBase('invoices', 'v2', invoiceId, 'approval');

        return this.http
            .get<IApprovalDto>(uri.toString(), { params: HttpExtras.IGNORE_CACHE })
            .pipe(map((dto: IApprovalDto) => new InvoiceApprovalInfo(dto)));
    }

    public validateInvoice(invoiceId: string): Observable<IInvoiceValidationResponse> {
        const uri: UriBuilder = this.env.getBase('invoices', 'v2', invoiceId, 'validate');
        // Backend now always returns 200 with IInvoiceValidationResponse (even when invalid),
        // so we can call it directly without custom error handling.
        return this.http.get<IInvoiceValidationResponse>(uri.toString(), {
            params: HttpExtras.CACHE_KEY_PREFIX(HttpCacheKeyPrefix.INVOICE),
        });
    }
}
